import { api } from './api';

export interface RecentActivity {
  type: string;
  action: string;
  title: string;
  description?: string;
  timestamp: string;
}

export interface StatCard {
  title: string;
  value: number;
  icon: string;
  color: string;
  changePercent?: number;
}

export interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  waiting: number;
  resolved: number;
  closed: number;
  critical: number;
  high: number;
}

export interface SlaStats {
  totalWithSla: number;
  onTime: number;
  breached: number;
  atRisk: number;
  complianceRate: number;
}

export interface PriorityDistribution {
  critical: number;
  high: number;
  medium: number;
  low: number;
  none: number;
}

export interface RecentTicket {
  id: string;
  ticketNumber: string;
  title: string;
  status: string;
  priority: string;
  assigneeName?: string;
  customerName?: string;
  createdAt: string;
}

export interface ExpiringLicense {
  id: string;
  name: string;
  productName?: string;
  expirationDate?: string;
  daysUntilExpiration: number;
}

export interface LicenseAlerts {
  totalLicenses: number;
  expiringCount: number;
  expiredCount: number;
  expiringLicenses: ExpiringLicense[];
}

export interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalCustomers: number;
  activeCustomers: number;
  totalTeams: number;
  totalRoles: number;
  ticketStats: TicketStats;
  slaStats: SlaStats;
  priorityDistribution: PriorityDistribution;
  licenseAlerts: LicenseAlerts;
  recentTickets: RecentTicket[];
  recentActivities: RecentActivity[];
  statCards: StatCard[];
}

export interface RevenueStats {
  activeContractsValue: number;
  monthlyRecurringRevenue: number;
  activeContracts: number;
  expiringContractsThisMonth: number;
}

export interface AdminDashboardStats {
  ticketStats: {
    totalTickets: number;
    openTickets: number;
    inProgressTickets: number;
    resolvedToday: number;
    resolvedThisWeek: number;
    resolvedThisMonth: number;
    overdueTickets: number;
    averageResolutionHours: number;
  };
  customerStats: {
    totalCustomers: number;
    activeCustomers: number;
    newCustomersThisMonth: number;
    customersWithOpenTickets: number;
  };
  userStats: {
    totalUsers: number;
    activeUsers: number;
    onlineUsers: number;
    techniciansAvailable: number;
  };
  revenueStats: RevenueStats;
  recentTickets: Array<{
    id: string;
    ticketNumber: string;
    title: string;
    customerName: string;
    statusName: string;
    priorityName: string;
    priority: number;
    assignedToName?: string;
    createdAt: string;
    timeAgo: string;
  }>;
  recentActivities: Array<{
    id: string;
    type: string;
    description: string;
    userName?: string;
    createdAt: string;
    timeAgo: string;
  }>;
  ticketsByStatus: Array<{
    status: string;
    count: number;
    color: string;
  }>;
  ticketsByPriority: Array<{
    priority: string;
    count: number;
    color: string;
  }>;
}

export const dashboardService = {
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get<DashboardStats>('/api/dashboard/stats');
    return response.data;
  },

  getAdminStats: async (): Promise<AdminDashboardStats> => {
    const response = await api.get<AdminDashboardStats>('/api/admin/admindashboard');
    return response.data;
  },
};
